import React, { useMemo } from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, ViewStyle, TextStyle } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'outline';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

/**
 * Reusable button component with marketplace design system variants
 */
export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  style,
  textStyle,
}) => {
  const mc = useMarketplaceColors();
  const isDisabled = disabled || isLoading;

  const styles = useMemo(() => StyleSheet.create({
    container: { borderRadius: 8, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8 },
    fullWidth: { width: '100%' },
    text: { fontWeight: '500' },
    primaryContainer: { backgroundColor: mc.accentBlueSolid },
    primaryDisabled: { backgroundColor: mc.buttonDisabled },
    primaryText: { color: mc.textPrimary },
    primaryTextDisabled: { color: mc.buttonDisabledText },
    secondaryContainer: { backgroundColor: 'transparent', borderWidth: 1, borderColor: mc.accentBlueSolid },
    secondaryDisabled: { borderColor: mc.buttonDisabled },
    secondaryText: { color: mc.accentBlue },
    secondaryTextDisabled: { color: mc.textMuted },
    dangerContainer: { backgroundColor: mc.accentRed },
    dangerDisabled: { backgroundColor: mc.buttonDisabled },
    dangerText: { color: mc.background },
    dangerTextDisabled: { color: mc.buttonDisabledText },
    outlineContainer: { backgroundColor: 'transparent', borderWidth: 1, borderColor: mc.textPrimary },
    outlineDisabled: { borderColor: mc.buttonDisabled },
    outlineText: { color: mc.textPrimary },
    outlineTextDisabled: { color: mc.textMuted },
  }), [mc]);

  const getContainerStyle = (): ViewStyle[] => {
    const baseStyle: ViewStyle[] = [styles.container, sizeStyles[size].container];
    if (fullWidth) baseStyle.push(styles.fullWidth);
    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryContainer);
        if (isDisabled) baseStyle.push(styles.primaryDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryContainer);
        if (isDisabled) baseStyle.push(styles.secondaryDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerContainer);
        if (isDisabled) baseStyle.push(styles.dangerDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineContainer);
        if (isDisabled) baseStyle.push(styles.outlineDisabled);
        break;
    }
    if (style) baseStyle.push(style);
    return baseStyle;
  };

  const getTextStyle = (): TextStyle[] => {
    const baseStyle: TextStyle[] = [styles.text, sizeStyles[size].text];
    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryText);
        if (isDisabled) baseStyle.push(styles.primaryTextDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryText);
        if (isDisabled) baseStyle.push(styles.secondaryTextDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerText);
        if (isDisabled) baseStyle.push(styles.dangerTextDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineText);
        if (isDisabled) baseStyle.push(styles.outlineTextDisabled);
        break;
    }
    if (textStyle) baseStyle.push(textStyle);
    return baseStyle;
  };

  const getLoadingColor = (): string => {
    switch (variant) {
      case 'primary': return mc.textPrimary;
      case 'secondary': return mc.accentBlueSolid;
      case 'danger': return mc.background;
      case 'outline': return mc.textPrimary;
      default: return mc.textPrimary;
    }
  };

  return (
    <TouchableOpacity style={getContainerStyle()} onPress={onPress} activeOpacity={0.8} disabled={isDisabled}>
      {isLoading ? (
        <ActivityIndicator size="small" color={getLoadingColor()} />
      ) : (
        <Text style={getTextStyle()}>{title}</Text>
      )}
    </TouchableOpacity>
  );
};

// Size configurations (no colors, stays static)
const sizeStyles = {
  small: StyleSheet.create({ container: { height: 32, paddingHorizontal: 12 }, text: { fontSize: 12, lineHeight: 18 } }),
  medium: StyleSheet.create({ container: { height: 40, paddingHorizontal: 16 }, text: { fontSize: 14, lineHeight: 21 } }),
  large: StyleSheet.create({ container: { height: 48, paddingHorizontal: 24 }, text: { fontSize: 16, lineHeight: 24 } }),
};
